import { useEffect, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import { ApiError } from '../../auth/api-client';
import { emberApi, type HowItWentPage as PageData, type PrecedentReaction } from '../api';
import { navigate } from '../router';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import './HandoffPage.css';
import './ReadPage.css';
import './HowItWentPage.css';

/**
 * How it went for others — `/how-it-went/:id` (v4.0 §4.22, 2026-10-05 拍板).
 * The page Ember put in front of a person standing in an uncertainty
 * reasoning cannot close from inside: a few other people here stood in it
 * and kept telling Ember what came after, and this is what Ember followed —
 * one entry per person, in Ember's own words, no names, no quotes. Under it,
 * where a teller said yes to sharing, the door to their own words (a read,
 * §4.9; registered only), and one question.
 *
 * Theirs only: a 404 to anyone else; a guest's by their browser. Opening it
 * is the read (the server stamps opened_at).
 */
export function HowItWentPage({ precedentId }: { precedentId: string }): ReactElement {
  const [data, setData] = useState<PageData | null>(null);
  const [failed, setFailed] = useState<false | 'not_found' | 'load_failed'>(false);
  const [opening, setOpening] = useState<string | null>(null);
  // The Embers the edge would not give again (410 not_available: the teller closed the read, or took the Ember back):
  // the door gave no sign of it until now (dev e2e 2026-10-19), so the page says what the read page would.
  const [closed, setClosed] = useState<Set<string>>(() => new Set());
  const [answering, setAnswering] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    emberApi.howItWent(precedentId)
      .then((r) => { if (alive) setData(r); })
      .catch((err: unknown) => {
        if (!alive) return;
        const s = err instanceof ApiError ? err.status : 0;
        if (s === 404) { setFailed('not_found'); trackFailed('precedent_load', 'not_found'); } else { setFailed('load_failed'); trackFailed('precedent_load', 'load_failed'); }
      });
    return () => { alive = false; };
  }, [precedentId]);

  // The door to a teller's own words: the Ember is given to read, and the read page opens.
  const openRead = async (emberId: string, readId: string | null): Promise<void> => {
    if (readId) { navigate(`/read/${encodeURIComponent(readId)}`); return; }
    setOpening(emberId);
    try {
      const { read_id } = await emberApi.howItWentRead(precedentId, emberId);
      navigate(`/read/${encodeURIComponent(read_id)}`);
    } catch (err: unknown) {
      const s = err instanceof ApiError ? err.status : 0;
      trackFailed('precedent_read', s === 410 ? 'not_available' : 'failed');
      if (s === 410) setClosed((c) => new Set(c).add(emberId));
      setOpening(null);
    }
  };

  const answer = async (value: PrecedentReaction): Promise<void> => {
    if (answering) return;
    setAnswering(true);
    try {
      const { reaction } = await emberApi.howItWentReact(precedentId, value);
      setData((d) => (d ? { ...d, reaction } : d));
    } catch {
      trackFailed('precedent_reaction', 'failed');
    } finally {
      setAnswering(false);
    }
  };

  if (failed === 'not_found') return <PageFailure title="This isn’t here." detail="It may have been given to someone else, or the link is old." logState="not_found" />;
  if (failed) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!data) return <PageLoading />;

  const { title, lede, entries, reaction } = data;

  return (
    <main className="ember-container ember-reading handoff-page read-page how-it-went">
      <div className="read-toolbar">
        <PageBack to="/talk?mode=chat" className="read-back" fallbackLabel="Back" />
        <div className="ember-toolbar-actions"><PageHome /></div>
      </div>
      <header className="handoff-head">
        <p className="read-kicker">How it went for others</p>
        <h1 className="handoff-title" dir="auto">{title}</h1>
        {lede && <p className="hiw-lede" dir="auto">{lede}</p>}
      </header>

      {entries.map((e, i) => (
        <article className="hiw-entry" key={i}>
          <h2 className="hiw-way" dir="auto">{e.heading}</h2>
          {e.told && e.told.times > 1 && (
            <p className="hiw-told">Told to Ember {e.told.times} times over {spanOf(e.told.days)}</p>
          )}
          {e.body.map((p, j) => <p className="handoff-para hiw-then" dir="auto" key={j}>{p}</p>)}
          {e.ember && closed.has(e.ember.id) && (
            <p className="hiw-told hiw-read-closed" dir="auto">The person who told it closed it.</p>
          )}
          {e.ember && !closed.has(e.ember.id) && (
            <button
              type="button"
              className="hiw-read-own"
              data-log="precedent_read_open"
              data-log-precedent-id={precedentId}
              data-log-ember-id={e.ember.id}
              disabled={opening === e.ember.id}
              onClick={() => { void openRead(e.ember!.id, e.ember!.read_id); }}
            >
              <span>{e.ember.read_id ? 'Read it again in their own words' : 'Read it in their own words'}</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          )}
        </article>
      ))}

      <p className="handoff-colophon hiw-colophon">
        <span className="handoff-seal" aria-hidden><span>seen<br />here</span></span>
        <span>Ember’s own words about what it has seen here. No one’s name, no one’s words. Different lives — it can’t say what will happen in yours.</span>
      </p>

      <section className="hiw-ask" aria-labelledby="hiw-ask-title">
        <p id="hiw-ask-title" className="hiw-ask-q">Did this show you something you hadn’t considered?</p>
        <div className="hiw-ask-opts" role="group" aria-labelledby="hiw-ask-title">
          {(['yes', 'a_little', 'no'] as const).map((v) => (
            <button
              type="button"
              key={v}
              className={`hiw-ask-opt${reaction === v ? ' is-chosen' : ''}`}
              aria-pressed={reaction === v}
              disabled={answering}
              data-log="precedent_reaction"
              data-log-state={v}
              data-log-precedent-id={precedentId}
              onClick={() => { void answer(v); }}
            >
              {v === 'yes' ? 'Yes' : v === 'a_little' ? 'A little' : 'No'}
            </button>
          ))}
        </div>
        <p className="hiw-ask-note">{reaction ? 'Noted. It stays between you and Ember.' : 'One tap, and it stays between you and Ember.'}</p>
      </section>
    </main>
  );
}

/** "6 weeks", "11 days", "4 months": the stretch a teller kept telling it over. */
function spanOf(days: number): string {
  if (days < 14) return `${Math.max(1, days)} day${days === 1 ? '' : 's'}`;
  if (days < 70) { const w = Math.round(days / 7); return `${w} week${w === 1 ? '' : 's'}`; }
  const m = Math.round(days / 30); return `${m} month${m === 1 ? '' : 's'}`;
}
